import type { UserRowV2 } from "./sql";
import { applyFilters } from "../../lib/funnel/filters";
import type { Filters } from "../../lib/funnel/filters";
import { STAGES, daysSinceSignup, medianDaysToStage } from "./helpers";
import type { StageKey } from "./helpers";
import { PRIMARY, VIOLET, ORANGE, RED, STEEL, GRAY } from "../../lib/funnel/theme";

export const COHORT_COLORS: string[] = [PRIMARY, ORANGE, VIOLET, RED, STEEL, GRAY];

// Comparison operator for "days since signup → milestone" filters.
export type DayOp = "within" | "after" | "never";

export type TriState = "any" | "yes" | "no";

// One timing constraint: "user reached `stage` {within|after} `days` of signup",
// or "never reached `stage`". Multiple constraints combine with AND.
export type StageTiming = {
  id: string;
  stage: StageKey;
  op: DayOp;
  days: number;
};

export type CohortDerived = {
  // Per-stage timing constraints. AND-combined.
  stageTimings: StageTiming[];
  // Demo path inclusion.
  hadDemo: TriState;
  // Conversion outcome filter (independent of timing).
  convertedToPaid: TriState;
  // Exclude users whose company hit paid plan within N hours of their signup.
  // Mirrors the user's "exclude users on paid flow" guard.
  excludePaidWithinHoursOfSignup: { enabled: boolean; hours: number };
  // Require that the user reached this stage at all (lifetime). "any" = no constraint.
  reachedStage: StageKey | "any";
};

export type CohortDef = {
  id: string;
  label: string;
  color: string;
  filters: Filters;
  derived: CohortDerived;
};

export const DEFAULT_DERIVED: CohortDerived = {
  stageTimings: [],
  hadDemo: "any",
  convertedToPaid: "any",
  excludePaidWithinHoursOfSignup: { enabled: false, hours: 24 },
  reachedStage: "any",
};

let timingCounter = 0;
export const newTimingId = () => `t${++timingCounter}`;

function hoursBetween(aIso: string | null, bIso: string | null): number | null {
  if (!aIso || !bIso) return null;
  const a = new Date(aIso).getTime();
  const b = new Date(bIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return (b - a) / 3600000;
}

export function applyCohort(rows: UserRowV2[], cohort: CohortDef): UserRowV2[] {
  const base = applyFilters(rows, cohort.filters);
  const d = cohort.derived;
  return base.filter((u) => {
    for (const t of d.stageTimings) {
      const stage = STAGES.find((s) => s.key === t.stage);
      if (!stage) continue;
      const days = daysSinceSignup(u, stage.dateOf(u));
      const hit = days != null;
      if (t.op === "never") { if (hit) return false; }
      else if (t.op === "within") { if (!hit || (days as number) > t.days) return false; }
      else if (t.op === "after") { if (!hit || (days as number) <= t.days) return false; }
    }
    if (d.hadDemo === "yes" && !u.had_demo) return false;
    if (d.hadDemo === "no" && u.had_demo) return false;
    if (d.convertedToPaid === "yes" && !u.converted_to_paid_at) return false;
    if (d.convertedToPaid === "no" && u.converted_to_paid_at) return false;
    if (d.excludePaidWithinHoursOfSignup.enabled && u.converted_to_paid_at) {
      const h = hoursBetween(u.created_at_utc, u.converted_to_paid_at);
      if (h != null && h <= d.excludePaidWithinHoursOfSignup.hours) return false;
    }
    if (d.reachedStage !== "any") {
      const stage = STAGES.find((s) => s.key === d.reachedStage);
      if (!stage) return true;
      if (!stage.dateOf(u)) return false;
    }
    return true;
  });
}

export type CohortMeasures = {
  size: number;
  reached: Partial<Record<StageKey, number>>;
  medianDays: Partial<Record<StageKey, number | null>>;
  paidConversionPct: number;
  medianDaysToPaid: number | null;
};

export function computeMeasures(users: UserRowV2[]): CohortMeasures {
  const size = users.length;
  const reached: Partial<Record<StageKey, number>> = {};
  const medianDays: Partial<Record<StageKey, number | null>> = {};
  for (const s of STAGES) {
    let n = 0;
    for (const u of users) if (s.dateOf(u)) n += 1;
    reached[s.key] = n;
    medianDays[s.key] = medianDaysToStage(users, s);
  }
  const paid = users.filter((u) => u.on_paid_plan).length;
  const paidStage = STAGES.find((s) => s.key === "paid")!;
  return {
    size,
    reached,
    medianDays,
    paidConversionPct: size === 0 ? 0 : (paid / size) * 100,
    medianDaysToPaid: medianDaysToStage(users, paidStage),
  };
}
